import React, { Component } from 'react';
import { arrayOf, bool, func, object, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, useIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { createResourceLocatorString } from '../../util/routes';
import { isMainSearchTypeKeywords } from '../../util/search';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { parse } from '../../util/urlHelpers';

import {
  Heading,
  Page,
  LayoutSingleColumn,
  Avatar,
  NamedLink,
  GamificationBadge,
  PaginationLinks
} from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';


import css from './AthletesListPage.module.css';

export class AthletesListPageComponent extends Component {
  constructor(props) {
    super(props);
    // The StaticRouter component used in server side rendering
    // provides the context object. We attach a `notfound` flag to
    // the context to tell the server to change the response status
    // code into a 404.
    this.props.staticContext.notfound = true;
  }

  render() {
    const {
      history,
      routeConfiguration,
      marketplaceName,
      intl,
      scrollingDisabled,
      athletes,
      pagination,
      location
    } = this.props;

    const title = intl.formatMessage({
      id: 'AthletesListPage.title',
    });

    const renderAthletes = (users) => {
      return users.map((u, index) => {
        const firstName = u.attributes.profile.firstName;
        const lastName = u.attributes.profile.lastName;
        const badgeName = u?.attributes?.profile?.publicData?.badgeName;

        return (
          <li key={index} className={css.usersItem}>
            <NamedLink className={css.link} name="ProfilePage" params={{ id: u.id.uuid }}>
              <div className={css.dataHolder}>
                <Avatar className={css.avatar} user={u} />
                <div className={css.name}>
                  <span>{firstName}</span> <span>{lastName}</span>
                </div>
              </div>
              <GamificationBadge titleClassName={css.titleBadge} itemClassName={css.mobileProfileItem} className={css.mobileBadge} badgeName={badgeName} />
            </NamedLink>
          </li>
        );
      })
    }

    const paginationLinks =
      pagination && pagination.totalPages > 1 ? (
        <PaginationLinks
          className={css.pagination}
          pageName="AthletesListPage"
          pageSearchParams={parse(location.search)}
          pagination={pagination}
        />
      ) : null;

    return (
      <Page title={title} scrollingDisabled={scrollingDisabled}>
        <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
          <div className={css.root}>
            <div className={css.content}>
              <Heading as="h1" rootClassName={css.heading}>
                <FormattedMessage id="AthletesListPage.heading" />
              </Heading>
              <p className={css.description}>
                <FormattedMessage id="AthletesListPage.description" values={{ marketplaceName }} />
              </p>

              {athletes.length > 0 ? <ul className={css.usersList}>{renderAthletes(athletes)}</ul> : <div className={css.loader}>Loading...</div>}
              {paginationLinks}
            </div>
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }
}

AthletesListPageComponent.defaultProps = {
  staticContext: {},
};

AthletesListPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  marketplaceName: string.isRequired,
  isKeywordSearch: bool.isRequired,

  // The context object from StaticRouter,
  // it should be manually passed if this page is rendered inside other route.
  staticContext: object,

  // from useIntl
  intl: intlShape.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
};

const EnhancedAthletesListPage = props => {
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const history = useHistory();
  const location = useLocation();
  const intl = useIntl();

  return (
    <AthletesListPageComponent
      routeConfiguration={routeConfiguration}
      marketplaceName={config.marketplaceName}
      isKeywordSearch={isMainSearchTypeKeywords(config)}
      history={history}
      location={location}
      intl={intl}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { athletes, pagination } = state.AthletesListPage;

  return {
    athletes,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const AthletesListPage = compose(connect(mapStateToProps))(EnhancedAthletesListPage);

export default AthletesListPage;
